import React, { useMemo, useState } from 'react';

import {
  Alert,
  Autocomplete,
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import type { MarketingRouterRuleRecord } from '~/hooks/apis/promo/useMarketingRouterRules';
import { useMarketingRouterCampaignSignificances } from '~/hooks/apis/promo/useMarketingRouterSignificance';

import MarketingRouterComparisonConfidenceCell from './MarketingRouterComparisonConfidenceCell';
import MarketingRouterComparisonLiftCell from './MarketingRouterComparisonLiftCell';
import MarketingRouterTermLabel from './MarketingRouterTermLabel';
import {
  type CampaignComparisonEntry,
  MAX_COMPARED_CAMPAIGNS,
  getCampaignVisitors,
  getDefaultCampaignMetric,
  getRuleCampaignOptions,
  getUnreliableCampaigns,
  isCampaignUnreliable,
  toCampaignSelection,
} from './marketingRouterCampaignComparison';
import { getComparableMetrics, getComparisonVariantRows } from './marketingRouterComparison';
import {
  METRIC_DISPLAY,
  type MarketingRouterMetricKey,
  formatMetricTotal,
  formatMetricValue,
  formatVisitors,
} from './marketingRouterSignificanceDisplay';

/** Columns in the results table, so a grouping row can span all of them. */
const RESULT_COLUMN_COUNT = 6;

/**
 * One campaign's block of the results table: a heading row naming the
 * campaign, then its control and each of its treatments, all measured against
 * that campaign's own control.
 */
function CampaignResultRows({
  entry,
  metric,
  isLoading,
  isError,
}: {
  entry: CampaignComparisonEntry;
  metric: MarketingRouterMetricKey;
  isLoading: boolean;
  isError: boolean;
}) {
  const { campaign, significance } = entry;
  const rows = significance ? getComparisonVariantRows(significance, metric) : [];
  const isUnreliable = isCampaignUnreliable(entry);
  const warnings = significance?.health.warnings ?? [];

  return (
    <>
      <TableRow>
        <TableCell colSpan={RESULT_COLUMN_COUNT} sx={{ bgcolor: 'action.hover' }}>
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="subtitle2" component="span">
              {campaign}
            </Typography>
            {significance ? (
              <Typography variant="caption" color="text.secondary">
                {formatVisitors(getCampaignVisitors(significance))} visitors
              </Typography>
            ) : null}
            {isUnreliable ? <Chip label="Traffic split broken" color="error" size="small" /> : null}
          </Stack>
        </TableCell>
      </TableRow>
      {isUnreliable ? (
        <TableRow>
          <TableCell colSpan={RESULT_COLUMN_COUNT}>
            <Alert severity="error">
              Traffic from “{campaign}” is not splitting between variants the way the rule is configured, so every
              number in this block is unreliable.
            </Alert>
          </TableCell>
        </TableRow>
      ) : null}
      {warnings.map((warning) => (
        <TableRow key={`${campaign}-${warning}`}>
          <TableCell colSpan={RESULT_COLUMN_COUNT}>
            <Alert severity="warning">{warning}</Alert>
          </TableCell>
        </TableRow>
      ))}
      {isLoading ? (
        <TableRow>
          <TableCell colSpan={RESULT_COLUMN_COUNT}>
            <Typography color="text.secondary" variant="body2">
              Loading results…
            </Typography>
          </TableCell>
        </TableRow>
      ) : null}
      {isError ? (
        <TableRow>
          <TableCell colSpan={RESULT_COLUMN_COUNT}>
            <Alert severity="error">Results for “{campaign}” could not be loaded.</Alert>
          </TableCell>
        </TableRow>
      ) : null}
      {!isLoading && !isError && rows.length === 0 ? (
        <TableRow>
          <TableCell colSpan={RESULT_COLUMN_COUNT}>
            <Typography color="text.secondary" variant="body2">
              No {METRIC_DISPLAY[metric].proseName} results for this campaign yet.
            </Typography>
          </TableCell>
        </TableRow>
      ) : null}
      {rows.map((row) => (
        <TableRow key={`${campaign}-${row.variantId}`} hover>
          <TableCell>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2">{row.variantName}</Typography>
              {row.isControl ? <Chip label="Control" size="small" variant="outlined" /> : null}
            </Stack>
          </TableCell>
          <TableCell align="right">{formatVisitors(row.visitors)}</TableCell>
          <TableCell align="right">{formatMetricValue(metric, row.value)}</TableCell>
          <TableCell align="right">{formatMetricTotal(metric, row.total)}</TableCell>
          <TableCell>
            <MarketingRouterComparisonLiftCell row={row} />
          </TableCell>
          <TableCell>
            <MarketingRouterComparisonConfidenceCell row={row} />
          </TableCell>
        </TableRow>
      ))}
    </>
  );
}

export default function MarketingRouterCampaignComparisonSection({
  rule,
  loyaltyOfferTypes,
  primaryMetric,
}: {
  rule: MarketingRouterRuleRecord;
  /** The page's product scope, so these numbers count the same products as the rest of the page. */
  loyaltyOfferTypes: string;
  primaryMetric: MarketingRouterMetricKey | undefined;
}) {
  const campaignOptions = useMemo(() => getRuleCampaignOptions(rule.match), [rule.match]);
  // Starts empty: each campaign is a full significance query, so none run
  // until the operator asks for them.
  const [campaigns, setCampaigns] = useState<Array<string>>([]);

  const significanceQueries = useMarketingRouterCampaignSignificances({
    marketingRouterId: rule.marketingRouterId,
    ruleId: rule.id,
    loyaltyOfferTypes,
    campaigns,
  });

  const entries = useMemo<Array<CampaignComparisonEntry>>(
    () => campaigns.map((campaign, index) => ({ campaign, significance: significanceQueries[index]?.data })),
    [campaigns, significanceQueries],
  );
  const metrics = useMemo(() => getComparableMetrics(entries), [entries]);
  const unreliableCampaigns = useMemo(() => getUnreliableCampaigns(entries), [entries]);

  // Left unset until the operator picks one, so the table follows the rule's
  // primary metric as results arrive.
  const [chosenMetric, setChosenMetric] = useState<MarketingRouterMetricKey | null>(null);
  const defaultMetric = useMemo(() => getDefaultCampaignMetric(entries, primaryMetric), [entries, primaryMetric]);
  const metric = chosenMetric !== null && metrics.includes(chosenMetric) ? chosenMetric : defaultMetric;

  const isAtLimit = campaigns.length >= MAX_COMPARED_CAMPAIGNS;

  return (
    <Stack spacing={1.5}>
      <Typography variant="h2">Compare campaigns</Typography>
      <Typography variant="body2" color="text.secondary">
        See this rule's results for each campaign side by side. Each visitor counts under the campaign of their first
        visit, so no visitor is counted in two campaigns.
      </Typography>
      <Autocomplete
        multiple
        // A rule with no campaign constraint matches every campaign, so the
        // operator can type any campaign, not just the ones the rule lists.
        freeSolo
        options={campaignOptions}
        value={campaigns}
        // Once the limit is reached only removing a campaign is allowed.
        getOptionDisabled={() => isAtLimit}
        onChange={(_, nextCampaigns) => setCampaigns(toCampaignSelection(nextCampaigns))}
        renderTags={(tagValues, getTagProps) =>
          tagValues.map((value, index) => {
            const { key, ...tagProps } = getTagProps({ index });
            return <Chip key={key} label={value} size="small" {...tagProps} />;
          })
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="Campaigns"
            size="small"
            placeholder={isAtLimit ? undefined : 'Choose or type a campaign'}
            helperText={
              isAtLimit
                ? `Up to ${MAX_COMPARED_CAMPAIGNS} campaigns can be compared at once.`
                : campaignOptions.length === 0
                  ? 'This rule matches every campaign. Type a utm_campaign value and press Enter.'
                  : 'Pick from the rule’s campaigns, or type another and press Enter.'
            }
            inputProps={{ ...params.inputProps, readOnly: isAtLimit }}
          />
        )}
        sx={{ maxWidth: 640 }}
      />

      {unreliableCampaigns.length > 0 ? (
        <Alert severity="error">
          {unreliableCampaigns.join(', ')} failed the sample-ratio check: traffic is not splitting between variants the
          way the rule is configured. Don't read anything into{' '}
          {unreliableCampaigns.length === 1 ? 'that campaign’s' : 'those campaigns’'} numbers below.
        </Alert>
      ) : null}

      {campaigns.length === 0 ? null : metrics.length > 0 && metric ? (
        <>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={metric}
            onChange={(_, next: MarketingRouterMetricKey | null) => {
              if (next) {
                setChosenMetric(next);
              }
            }}
            aria-label="Metric to compare across campaigns"
          >
            {metrics.map((key) => (
              <ToggleButton key={key} value={key}>
                {METRIC_DISPLAY[key].name}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Card>
            <CardContent>
              <TableContainer>
                <Table size="small" aria-label={`${METRIC_DISPLAY[metric].name} by campaign`}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: '22%' }}>Variant</TableCell>
                      <TableCell align="right">Visitors</TableCell>
                      <TableCell align="right">{METRIC_DISPLAY[metric].perVisitorLabel}</TableCell>
                      <TableCell align="right">{METRIC_DISPLAY[metric].totalLabel}</TableCell>
                      <TableCell sx={{ width: '16%' }}>Lift vs control</TableCell>
                      <TableCell sx={{ width: '18%' }}>
                        <MarketingRouterTermLabel term="confidence">Confidence</MarketingRouterTermLabel>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {entries.map((entry, index) => (
                      <CampaignResultRows
                        key={entry.campaign}
                        entry={entry}
                        metric={metric}
                        isLoading={significanceQueries[index]?.isLoading ?? false}
                        isError={significanceQueries[index]?.isError ?? false}
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Every lift and confidence is measured against that campaign's own control. Results are shown side by
                  side, never pooled across campaigns.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </>
      ) : significanceQueries.some((significanceQuery) => significanceQuery.isLoading) ? (
        <Typography color="text.secondary">Loading campaign results…</Typography>
      ) : significanceQueries.some((significanceQuery) => significanceQuery.isError) ? (
        <Alert severity="error">Campaign results could not be loaded.</Alert>
      ) : (
        <Typography color="text.secondary">None of the selected campaigns have results yet.</Typography>
      )}
    </Stack>
  );
}
